<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { AppState } from '../../etc/stores';
  import {
    formatMoneyToParts,
    getHeightHeaderDash
  } from '../../etc/utils';
  import ThemedIcon from './ThemedIcon.svelte';
  import CloseIcon from '../icons/Close.svelte';
  import CoinStatusBox from './CoinStatusBox.svelte';
  import Analytics from '../../services/analytics';
  import { _ } from 'svelte-i18n';
  import Label from '../typography/Label.svelte';
  import AvailableBalance from './AvailableBalance.svelte';
  import { replaceVariables } from '../../i18n';
  import { Container } from '../../services/container';
  import { fade, fly } from 'svelte/transition';
  import { Customer } from '../../services/customer';

  const dispatch = createEventDispatcher();

  export let NoWalletAmount = false;
  export let startNavOutAnimation = false;
  export let startNavInAnimation = false;
  export let timeNavAnimation = 0;

  const customer = Container.get(Customer);

  $: amount = ['number', 'string'].includes(
    typeof $AppState.customer.wallet?.coins
  )
    ? $AppState.customer.wallet?.coins
    : null;

  const getAmount = () => {
    if (amount) {
      return formatMoneyToParts(
        $AppState.business.location,
        Number(amount).toFixed(2)
      )
        .map(({ type, value }) => {
          switch (type) {
            case 'currency':
              return `<small>${value}</small>`;
            default:
              return value;
          }
        })
        .join('');
    }

    return '...';
  };

  let pendingCoins: number | string = Number(customer.getPendingCoins()).toFixed(2);
  let expiringCoins: number | string = Number(customer.getExpiringCoins()).toFixed(2);

  const heightHeaderDash = getHeightHeaderDash(pendingCoins);

  const onGAEventClose = () => {
    Analytics.logEvent(
      'click-close',
      Analytics.getIsGuest() ? 'guest-state' : 'join-state'
    );
  };

  const closePopover = () => {
    onGAEventClose();
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: false
      }
    }));
  };
</script>

<div
  class="dashboard__header twism-pending-coins {NoWalletAmount ? '-no-wallet-amount' : ''}"
>
  <div
    class="dashboard__header-background"
    class:--animation={startNavOutAnimation}
    style={`--animation-time: ${
      timeNavAnimation / 2
    }ms;--animation-translate: ${-(heightHeaderDash - 62)}px`}
  />
  <div
    class="dashboard__header-wrapper"
    class:--animation={startNavOutAnimation}
    style={`--animation-time: ${timeNavAnimation / 2}ms`}
    in:fade={{
      duration: startNavInAnimation ? 200 : 0,
      delay: startNavInAnimation ? 100 : 0
    }}
  >
    <div class="dashboard__close-twism" on:click={closePopover}>
      <ThemedIcon>
        <CloseIcon />
      </ThemedIcon>
    </div>
    <img
      class="dashboard__logo"
      src={$AppState.business.logo.publicPath.small}
      alt="Business Logo"
    />
    <Label type="default-medium" color="#FFF" class="dashboard__username">
      Hello, {$AppState.customer.name}!
    </Label>
    <div class="dashboard__body">
      <AvailableBalance class="dashboard__amount" amount={getAmount()} />
      <CoinStatusBox
        pendingAmount={String(pendingCoins)}
        expiringAmount={String(expiringCoins)}
        on:click={() => dispatch('coin-status-clicked')}
      />
      {#if $AppState.business.loyaltyEnabled}
        <div class="dashboard__loyalty">
          <Label type="medium-regular" color="#FFF">
            {replaceVariables($_('header.coin_for_every'), {
              LOYALTY_PERCENTAGE: Number(
                $AppState.business.rewardsSettings.coinRewardPercentage
              )
            })}
          </Label>
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  :global(.dashboard__amount) {
    font-size: 32px !important;
    line-height: 40px !important;
    letter-spacing: -0.01em !important;
  }
  :global(.dashboard__amount small) {
    font-size: 20px;
    line-height: 18px;
    font-weight: 400;
    color: rgba(#fff, 0.8);
    margin-right: 4px;
    display: inline-block;
    vertical-align: middle;
  }

  :global(.available-balance__amount__icon) {
    margin-bottom: 10px;
  }

  :global(.dashboard__username) {
    font-weight: 500 !important;
    letter-spacing: 0.02em !important;
    position: absolute !important;
    top: 32px !important;
  }

  .dashboard {
    &__header {
      width: 100%;
      min-height: 185px;
      box-sizing: border-box;
      padding: 72px 0 16px;
      border-radius: 20px 20px 0 0;
      position: relative;
      isolation: isolate;

      &-background {
        @keyframes fly-animation {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(var(--animation-translate));
          }
        }

        position: absolute;
        inset: 0;
        background: var(--twism-theme-background);
        background-size: cover;
        z-index: -1;

        &.--animation {
          animation: fly-animation var(--animation-time) forwards;
          animation-delay: var(--animation-time);
        }
      }

      &-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;

        transition: opacity var(--animation-time) ease-in-out;
        &.--animation {
          opacity: 0;
        }
      }

      &.twism-pending-coins {
        padding: 65px 0 16px;
      }

      @media screen and (max-height: 568px) {
        padding-top: 80px;
      }
      @media screen and (max-width: 468px) {
        border-radius: 0;
      }

      &.-no-wallet-amount {
        padding-top: 88px;
        padding-bottom: 24px;
      }
    }

    &__close-twism {
      position: absolute;
      right: 24px;
      top: 24px;
      cursor: pointer;
      z-index: 9;
    }

    &__loyalty {
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
      align-items: center;
      margin-top: 16px;
    }

    &__logo {
      width: 32px;
      height: 32px;
      border: 1px solid var(--twism-theme-primary);
      box-sizing: border-box;
      border-radius: 50%;
      position: absolute;
      top: 24px;
      left: 24px;
    }

    &__body {
      align-items: center;
      flex-flow: column nowrap;
      display: flex;
      width: 100%;
      flex: 1;
    }

    &__balance {
      display: flex;
      align-items: flex-end;
      justify-content: center;
    }

    &__amount__icon {
      display: inline-block;
      vertical-align: middle;
      margin-bottom: 10px;
    }
  }
</style>
